import _ from 'underscore';

import { ISchedule } from '../../schedule/types/ISchedule';
import { FeatureFlags } from '../FeatureFlags';
import { FlowOutputStepRunStatus } from '../models/FlowOutputStepRunInfo';
import { RunFlowTaskInfo, RunFlowTaskStatus } from '../models/RunFlowTaskInfo';
import { FlowOutputStep } from '../ServerApi/types/FlowOutputStep';
import { FlowOutputStepRun } from '../ServerApi/types/FlowOutputStepRun';
import { FlowRun } from '../ServerApi/types/FlowRun';
import { FlowRunErrorUniqueKey } from '../ServerApi/types/FlowRunError';
import { IDataConnection } from '../ServerApi/types/IDataConnection';
import { ISiteNameWithId } from '../ServerApi/types/ISiteNameWithId';
import { INamedItem } from '../ServerApi/types/resourceParts';
import { RunFlowTask } from '../ServerApi/types/RunFlowTask';
import { FlowOutputStepMapper } from './FlowOutputStepMapper';
import { MapperHelper } from './MapperHelper';

export module RunFlowTaskMapper {

  const M = MapperHelper;

  export function map(
    runFlowTasks: ReadonlyArray<RunFlowTask>,
    schedule: _.Dictionary<ISchedule>,
    site: _.Dictionary<ISiteNameWithId>,
    flows: _.Dictionary<INamedItem>,

    dataConnections: _.Dictionary<IDataConnection>,
    flowOutputSteps: _.Dictionary<FlowOutputStep>,
    flowRuns: ReadonlyArray<FlowRun>,
    flowOutputStepRuns: ReadonlyArray<FlowOutputStepRun>): RunFlowTaskInfo[] {

    const flowOutputStepRunsByRun = _.groupBy(flowOutputStepRuns, 'flowRunId');
    const flowOutputStepRunsByOutputStepId = _.groupBy(flowOutputStepRuns, 'flowOutputStepId');
    const flowRunsGroupedByTaskId = _.groupBy(flowRuns, 'taskId');

    return _.map(runFlowTasks, (runFlowTask: RunFlowTask) => {
      const lastFlowRun = flowRunsGroupedByTaskId[runFlowTask.id] ? flowRunsGroupedByTaskId[runFlowTask.id][0] : null;
      let lastOutputStepRuns = lastFlowRun != null ? flowOutputStepRunsByRun[lastFlowRun.id] : null;
      const outputStepsForTask: FlowOutputStep[] = [];
      _.forEach(runFlowTask.flowOutputStepIds, (id) => {
        outputStepsForTask.push(flowOutputSteps[id]);
      });

      const isRunAllIncremental = !runFlowTask.runAllOutputs || runFlowTask.isRunAllIncremental === undefined ?
        null :
        runFlowTask.isRunAllIncremental;

      const isLinkedTask = runFlowTask.taskType === 'LinkedTask' && runFlowTask.taskType !== undefined;

      const status = getRunFlowTaskStatus(
        runFlowTask,
        isLinkedTask ?
          (runFlowTask.status ? runFlowTask.status.toUpperCase() : runFlowTask.status) :
          lastFlowRun ?
            lastFlowRun.status : ''
      );

      if (isLinkedTask && outputStepsForTask.length > 0) {
        const lastOutputStepRunsForLinkedTask: FlowOutputStepRun[] = [];
        const outputStepsForTaskGroupedByFlowId = _.groupBy(outputStepsForTask, 'flowId');
        _.forEach(runFlowTask.flowOutputStepIds, (id) => {
          const outputStepRuns = flowOutputStepRunsByOutputStepId[id];
          _.forEach(outputStepRuns, (run: FlowOutputStepRun) => {
            if (run && outputStepsForTaskGroupedByFlowId[run.flowId]) {
              lastOutputStepRunsForLinkedTask.push(run);
            }
          });
        });
        if (lastOutputStepRunsForLinkedTask) {
          lastOutputStepRuns = lastOutputStepRunsForLinkedTask;
        }
      }

      const flowTaskInfo: RunFlowTaskInfo = {
        id: runFlowTask.id,
        contentType: 'runFlowTask',
        taskType: isLinkedTask ? runFlowTask.taskType : 'FlowTask',
        stepNumber: isLinkedTask ? runFlowTask.linkedTaskFlowListItem.stepNumber : null,
        totalSteps: isLinkedTask ? runFlowTask.linkedTaskFlowListItem.totalSteps : null,
        linkedTaskId: isLinkedTask ? runFlowTask.linkedTaskId : null,
        flowOutputSteps: FlowOutputStepMapper.mapForRunFlowTask(outputStepsForTask, dataConnections, runFlowTask.flowRunSpecOutputStepParamsMap, isRunAllIncremental),
        priority: runFlowTask.priority,
        lastRunAt: lastFlowRun != null ? M.toDate(lastFlowRun.startedAt) : runFlowTask.lastRunAt != null ? M.toDate(runFlowTask.lastRunAt) : null,
        runNextAt: FeatureFlags.autoSuspendFlowTasksEnabled && runFlowTask.suspended ? null : M.toDate(runFlowTask.runNextAt),
        schedule: schedule[runFlowTask.scheduleId],
        site: site[runFlowTask.siteId],
        flow: !isLinkedTask ? flows[runFlowTask.targetId] : null,
        flowId: !isLinkedTask ? flows[runFlowTask.targetId].id : null,
        runAllOutputs: !isLinkedTask ? runFlowTask.runAllOutputs : null,
        isRunAllIncremental: !isLinkedTask ? isRunAllIncremental : null,
        suspended: runFlowTask.suspended,
        flowRunErrors: lastOutputStepRuns != null ?
          _.uniq(_.reduce(lastOutputStepRuns, (memo, run) => memo.concat(run.errors), []),
            false,
            FlowRunErrorUniqueKey) : [],
        ...!!FeatureFlags.autoSuspendFlowTasksEnabled && {
          status: status
        },
      };
      return flowTaskInfo;
    });
  }

  // TODO: how do we deduce status
  function getRunFlowTaskStatus(runFlowTask: RunFlowTask, status: string): RunFlowTaskStatus {
    if (runFlowTask.suspended) {
      return RunFlowTaskStatus.Suspended;
    }
    if (!status) {
      return RunFlowTaskStatus.Scheduled;
    }
    switch (status) {
      case FlowOutputStepRunStatus.success: {
        return RunFlowTaskStatus.Succeeded;
      }
      case FlowOutputStepRunStatus.failed: {
        return RunFlowTaskStatus.Failed;
      }
      case FlowOutputStepRunStatus.inProgress: {
        return RunFlowTaskStatus.InProgress;
      }
      default: {
        return RunFlowTaskStatus.Scheduled;
      }
    }
  }

}
